// npm install react-markdown remark-gfm remark-math rehype-katex katex axio firebase framer-motion rehype-raw sweetalert2  rehype-highlight react-icons

'use client';

import { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import Swal from "sweetalert2";
import 'katex/dist/katex.min.css';
import "highlight.js/styles/github.css";
import { AiOutlineInfoCircle,AiOutlineDelete, AiOutlineLoading3Quarters, AiOutlinePlusCircle } from 'react-icons/ai';
import { AuthProvider, db, useAuth } from '../contexts/AuthContext';
import UserMenu from '../components/UserMenu';
import AskLLMPopup from "../components/AskLLM";
import AddItemDialog from '../components/AddItemDialog';
import AboutDialog from '../components/AboutDialog';

// Thêm các provider đăng nhập mới
import { getAuth, signInWithPopup, FacebookAuthProvider, OAuthProvider } from "firebase/auth";

type SubjectsByGrade = Record<string, Record<string, { vi: string, en: string }[]>>;
const subjectsByGrade: SubjectsByGrade = require('../contexts/topics.json');

const subjectNameMap: Record<string, { vi: string, en: string }> = {
  "Toán học": { vi: "Toán học", en: "Mathematics" },
  "Vật lý": { vi: "Vật lý", en: "Physics" },
  "Hoá học": { vi: "Hoá học", en: "Chemistry" },
  "Tin học": { vi: "Tin học", en: "Informatic" },
  "Sinh học": { vi: "Sinh học", en: "Biology" },
  "Tiếng Anh": { vi: "Tiếng Anh", en: "English" },
  "Văn học": { vi: "Văn học", en: "Literature" },
  "Lịch sử": { vi: "Lịch sử", en: "History" },
  "Địa lý": { vi: "Địa lý", en: "Geography" },
  "Pháp luật": { vi: "Kinh tế và Pháp luật", en: "Law and Economics" }
};
const curiosityTypesList = [
  { vi: '🎨Thách thức sáng tạo', en: '🎨Creative challenges' },
  { vi: '🌍Kết nối kiến thức với thực tế', en: '🌍Connecting knowledge with real life' },
  { vi: '📖Tình huống bất ngờ, phi lý', en: '📖Unexpected stories and paradoxes' },
  { vi: '🧪Học qua trải nghiệm và thử nghiệm', en: '🧪Learning through experience and experimentation' },
  { vi: '🧩Trò chơi và giải đố', en: '🧩Games and puzzles' },
  { vi: '❓Câu hỏi mở và tình huống giả định', en: '❓Open-ended questions and hypothetical situations' },
  { vi: '🕵️Hiện tượng bí ẩn và sự kiện chưa giải thích được', en: '🕵️Mysterious phenomena and unexplained events' },
  { vi: '🔗Kết hợp kiến thức liên môn', en: '🔗Interdisciplinary knowledge integration' },
  { vi: '🔍Khám phá kiến thức mới và hiểu sâu bản chất sự vật', en: '🔍Discover new knowledge and understand how things work' },
  { vi: '👁️Quan sát hiện tượng lạ và kích thích giác quan', en: '👁️Observe strange phenomena and stimulate the senses' },
  { vi: '🧠Giải các bài toán hóc búa và thử thách tư duy logic', en: '🧠Solve challenging problems and test your logical thinking' },
  { vi: '👩‍🚀Khám phá nghề nghiệp tương lai và vai trò của kiến thức', en: '👩‍🚀Explore future careers and how knowledge applies to real jobs' }
];
const curiosityPrompts = [
  "Creative challenges: Think of a new way to solve this problem that no one has tried before; or If you were an inventor, how would you improve this idea?",
  "Real-life connection: Does this phenomenon happen in everyday life? Have you seen it before? or How could this knowledge help in real life or future work?",
  "Unexpected stories and paradoxes: What surprised you about this content? Why?; Do you think this goes against what you already know?",
  "Experiential learning: If you could try this in real life, where would you start?;Can you create a small experiment to test this knowledge?",
  "Games and puzzles: Turn this content into a game or riddle. What rules would you set? Can you solve this challenge?",
  "Open-ended questions and hypotheticals: What would happen if…? How many different ways can you approach this problem?",
  "Mysterious phenomena: What do you think caused this strange phenomenon? If you were a scientist, how would you investigate it?",
  "Interdisciplinary integration: Can you use knowledge from another subject to solve this? What could you understand better by combining Math, Physics, and Chemistry?",
  "Discover how things work: How does this work? Why does it happen like that? Can you explain the nature of this phenomenon in your own words?",
  "Stimulate senses: What stands out in this image/video/simulation? What was your first impression when seeing this phenomenon?",
  "Solve logic challenges: Can you find multiple ways to solve this problem? If you only had one minute to solve this, what would your strategy be?",
  "Explore careers: Which jobs use this knowledge the most? Would you be interested in one of them? If you became an engineer/doctor/researcher/..., which part of this knowledge would be most important?"
];
type Curiosity = {
  subject: string;
  topics: string[];
  goal: string;
  difficulty: string;
  types: string[];
  expectation: string;
  query: string;
};

const AiCopilotContent = () => {
  // State
  const [profile, setProfile] = useState({
    uid: "helix", name: 'Hà Nội', age: '15', grade: '9', personality: 'curiosity', interest: 'music', style: 'friendly', llm_api: 'gemini', language: "vi"
  });
  const [curiosity, setCuriosity] = useState<Curiosity>({
    subject: Object.keys(subjectsByGrade['9'])[0] || "Toán học", // Set default subject using key
    topics: [],
    goal: 'Discovery',
    difficulty: 'Medium',
    types: [],
    expectation: '',
    query: ''
  });

  // Add this useEffect to handle grade changes
  useEffect(() => {
    if (profile.grade && subjectsByGrade[profile.grade]) {
      // When grade changes, update subject to first available one in new grade
      const availableSubjects = Object.keys(subjectsByGrade[profile.grade]);
      if (availableSubjects.length > 0 && !availableSubjects.includes(curiosity.subject)) {
        setCuriosity(prev => ({
          ...prev,
          subject: availableSubjects[0],
          topics: [] // Reset topics when subject changes
        }));
      }
    }
  }, [profile.grade]);

  // Modify the subject selection handler
  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSubject = e.target.value;
    setCuriosity(prev => ({
      ...prev,
      subject: newSubject,
      topics: [] // Reset topics when subject changes
    }));
  };

  const t = (vi: string, en: string) => (profile.language === 'vi' ? vi : en);

  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showLLMPopup, setShowLLMPopup] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogMode, setAddDialogMode] = useState<'subject' | 'topic' | 'curiosityType'>('subject');
  const [newSubject, setNewSubject] = useState('');
  const [newSubjectEn, setNewSubjectEn] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [newTopicEn, setNewTopicEn] = useState('');
  const [newCuriosityType, setNewCuriosityType] = useState('');
  const [newCuriosityTypeEn, setNewCuriosityTypeEn] = useState('');
  const [showAbout, setShowAbout] = useState(false);
  const { user, login } = useAuth();
  const [history, setHistory] = useState<{ content: string; timestamp: number; id?: string }[]>([]);
  const [selectedOutput, setSelectedOutput] = useState<string | null>(null);
  const [firestoreError, setFirestoreError] = useState<string | null>(null);

  // Fetch history
  useEffect(() => {
    if (profile?.uid) fetchHistory(profile.uid);
  }, [profile?.uid]);

  const saveToHistory = async (content: string, userId: string) => {
    const item = { content, timestamp: Date.now() };
    try {
      const docRef = await addDoc(collection(db, 'histories', userId, 'queries'), item);
      setHistory(prev => [{ ...item, id: docRef.id }, ...prev]);
    } catch (err: any) {
      setFirestoreError(
        err?.code === "permission-denied"
          ? "Bạn không có quyền ghi lịch sử. Vui lòng liên hệ quản trị viên."
          : "Lỗi khi lưu lịch sử."
      );
    }
  };

  const fetchHistory = async (userId: string) => {
    try {
      const q = query(collection(db, 'histories', userId, 'queries'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      // setHistory(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      setHistory(snapshot.docs.map(doc => {
        const data = doc.data();
        return { content: data.content, timestamp: data.timestamp, id: doc.id };
      }));
      setFirestoreError(null);
    } catch (err: any) {
      setFirestoreError(
        err?.code === "permission-denied"
          ? "Bạn không có quyền xem lịch sử. Vui lòng liên hệ quản trị viên."
          : "Lỗi khi tải lịch sử."
      );
      setHistory([]);
    }
  };

  // Add subject/topic/curiosity type
  const handleAddSubject = () => {
    if (!newSubject.trim()) return;
    if (!subjectsByGrade[profile.grade][newSubject]) {
      subjectsByGrade[profile.grade][newSubject] = [];
      subjectNameMap[newSubject] = {
        vi: newSubject,
        en: newSubjectEn.trim() ? newSubjectEn : newSubject
      };
      setCuriosity({ ...curiosity, subject: newSubject, topics: [] });
    }
    setNewSubject('');
    setNewSubjectEn('');
  };

  const handleAddTopic = () => {
    const topicName = newTopic.trim();
    if (!topicName) return;
    const grade = profile.grade;
    const subjectKey = Object.keys(subjectsByGrade[grade]).find(s => {
      const mappedSubject = subjectNameMap[s];
      return mappedSubject?.vi === curiosity.subject || mappedSubject?.en === curiosity.subject || s === curiosity.subject;
    });
    if (!subjectKey) return;
    const topicObj = { vi: topicName, en: newTopicEn.trim() || topicName };
    if (!subjectsByGrade[grade][subjectKey]) subjectsByGrade[grade][subjectKey] = [];
    const isDuplicate = subjectsByGrade[grade][subjectKey].some(
      t => t.vi === topicName || t.en === topicObj.en
    );
    if (!isDuplicate) {
      subjectsByGrade[grade][subjectKey].push(topicObj);
      setCuriosity(prev => ({
        ...prev,
        topics: [...(prev.topics || []), topicObj.en]
      }));
    }
    setNewTopic('');
    setNewTopicEn('');
    setAddDialogOpen(false);
  };

  const handleAddCuriosityType = () => {
    if (!newCuriosityType.trim()) return;
    if (!curiosityTypesList.some(t => t.vi === newCuriosityType || t.en === newCuriosityType)) {
      curiosityTypesList.push({
        vi: newCuriosityType,
        en: newCuriosityTypeEn.trim() ? newCuriosityTypeEn : newCuriosityType
      });
      setCuriosity({
        ...curiosity,
        types: [...curiosity.types, t(newCuriosityType, newCuriosityTypeEn.trim() ? newCuriosityTypeEn : newCuriosityType)]
      });
    }
    setNewCuriosityType('');
    setNewCuriosityTypeEn('');
  };

  // Delete history
  const handleDeleteHistory = async (item: { content: string; timestamp: number; id?: string }) => {
    const confirm = await Swal.fire({
      title: t("Xác nhận xoá?", "Delete this item?"),
      text: t("Bạn có chắc muốn xoá vết tương tác này?", "Are you sure you want to delete this history item?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Xoá", "Delete"),
      cancelButtonText: t("Huỷ", "Cancel")
    });
    if (!confirm.isConfirmed) return;
    setHistory(prev => prev.filter(h => h.timestamp !== item.timestamp));
    if (item.id && profile?.uid) {
      try {
        const { doc, deleteDoc } = await import('firebase/firestore');
        await deleteDoc(doc(db, 'histories', profile.uid, 'queries', item.id));
        fetchHistory(profile.uid);
      } catch (err) {
        setFirestoreError("Lỗi khi xoá lịch sử trên máy chủ.");
      }
    }
    if (selectedOutput === item.content) setSelectedOutput(null);
  };

  // Main API call
  const reframeContents = async () => {
    setLoading(true);
    try {
      let types = curiosity.types;
      let promptValues: string[] = [];
      if (!types || types.length === 0 || (Array.isArray(types) && types[0] === "")) {
        promptValues = curiosityPrompts;
      } else {
        promptValues = types.map(idxStr => {
          const idx = parseInt(idxStr, 10);
          return curiosityPrompts[idx] || "";
        }).filter(Boolean);
      }
      let topics = curiosity.topics;
      if (!topics || topics.length === 0 || (Array.isArray(topics) && topics[0] === "")) {
        topics = subjectsByGrade[profile.grade][Object.keys(subjectsByGrade[profile.grade]).find(s => subjectNameMap[s]?.en === curiosity.subject) || curiosity.subject]
          .map(topic => topic.en);
      }
      const requestData = {
        age: profile.age ? parseInt(profile.age) : 16,
        name: profile.name,
        personality: profile.personality,
        interest: profile.interest,
        style: profile.style,
        llm_api: profile.llm_api,
        language: profile.language === 'vi' ? "vietnamese" : "english",
        grade: parseInt(profile.grade),
        subject: curiosity.subject,
        topics: topics.join(', ') || "Calculus, Differential Equations",
        curiosity_goal: curiosity.goal,
        curiosity_level: curiosity.difficulty,
        curiosity_types: promptValues.join(', ') || curiosityPrompts.join(', '),
        expectation: curiosity.expectation,
        query: ''
      };
      const response = await axios.post("/api/reframing", requestData, { timeout: 180000 });
      let result = '';
      if (response.data && response.data.output) result = response.data.output;
      else if (response.data && response.data.result) result = response.data.result;
      else if (typeof response.data === 'string') result = response.data;
      result = Array.isArray(result) ? result.join(" ") : result;

      // UI display text
      const subjectDisplayText =
        Object.entries(subjectNameMap).find(([, v]) => v.en === requestData.subject || v.vi === requestData.subject)
          ? t(
              subjectNameMap[
                Object.keys(subjectNameMap).find(
                  k => subjectNameMap[k].en === requestData.subject || subjectNameMap[k].vi === requestData.subject
                )!
              ].vi,
              subjectNameMap[
                Object.keys(subjectNameMap).find(
                  k => subjectNameMap[k].en === requestData.subject || subjectNameMap[k].vi === requestData.subject
                )!
              ].en
            )
          : requestData.subject;

      const topicsDisplayText = requestData.topics
        .split(',')
        .map(topicEn => {
          const subjectKey =
            Object.keys(subjectsByGrade[profile.grade]).find(
              s => subjectNameMap[s]?.en === requestData.subject || subjectNameMap[s]?.vi === requestData.subject
            ) || requestData.subject;
          const topicObj =
            (subjectsByGrade[profile.grade][subjectKey] || []).find(
              t => t.en.trim() === topicEn.trim() || t.vi.trim() === topicEn.trim()
            );
          return topicObj ? t(topicObj.vi, topicObj.en) : topicEn.trim();
        })
        .join(', ');

      const curiosityTypesDisplayText = (() => {
        if (curiosity.types && curiosity.types.length > 0) {
          return curiosity.types
            .map(idxStr => {
              const idx = parseInt(idxStr, 10);
              const item = curiosityTypesList[idx];
              return item ? t(item.vi, item.en) : '';
            })
            .filter(Boolean)
            .join(', ');
        }
        return curiosityTypesList.map(item => t(item.vi, item.en)).join(', ');
      })();

      result = "# " + subjectDisplayText + " - " + requestData.grade + " - " + topicsDisplayText + "\n ## *" + curiosityTypesDisplayText + "*\n\n" + result;
      setSelectedOutput(result);
      saveToHistory(result, profile.uid);
    } catch (err) {
      setLoading(false);
      console.error("Lỗi khi gọi API /api/curiosity:", err);
      if (axios.isAxiosError(err)) {
        setFirestoreError("Lỗi khi gọi API: " + (err.response?.data || err.message));
      } else {
        setFirestoreError("Unknown error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Context menu for AskLLM
  const handleContextMenu = (e: React.MouseEvent) => {
    const selection = window.getSelection()?.toString().trim();
    if (selection) {
      e.preventDefault();
      curiosity.query = selection;
      Swal.fire({
        title: t("Bạn có muốn hỏi AI Chatbot?", 'Ask AI Chatbot?'),
        text: `${t("Nội dung", 'Question')}: "${curiosity.query}"`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("Có", 'Yes'),
        cancelButtonText: t("Không", 'No')
      }).then((result) => {
        if (result.isConfirmed) setShowLLMPopup(true);
      });
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <h2 className="text-xl font-semibold mb-4">Bạn cần đăng nhập để sử dụng AI Copilot</h2>
        <div className="flex flex-col w-[260px]">
          <button
            onClick={login}
            className="bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700 mb-4 w-full"
            style={{ minWidth: 200 }}
          >
            Đăng nhập với Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center px-4 py-2 border-b bg-blue-600 text-white shadow-sm">
        <h1 className="text-2xl font-bold">{t('AI Copilot - TRỢ LÝ HỌC QUA LĂNG KÍNH TÒ MÒ', 'AI Copilot: Curiosity-Based Reframing of Textbook Content')}</h1>
        <div className="flex items-center space-x-4">
          <div
            onClick={() => setShowAbout(true)}
            className="flex items-center space-x-1 text-white cursor-pointer hover:underline"
          >
            <AiOutlineInfoCircle className="text-lg" />
            <span className="text-sm">{t('Thông tin', 'About')}</span>
          </div>
          <select value={profile.language} onChange={e => setProfile({ ...profile, language: e.target.value })} className="bg-white text-black border p-1 rounded">
            <option value="vi">🇻🇳 Tiếng Việt</option>
            <option value="en">🇺🇸 English</option>
          </select>
          <div className="text-black">
            <UserMenu profile={profile} setProfile={setProfile} />
          </div>
        </div>
      </div>

      <div className="flex flex-row px-4 py-4 space-x-4">
        {/* Panel P2 - Subject Info */}
        <div className="border rounded-xl p-4 shadow w-1/2">
          <div className="flex flex-row items-center gap-3 mb-4">
            <h2 className="font-semibold text-lg whitespace-nowrap">{t('Chủ đề môn học', 'Subject Info')}</h2>
            <div className="flex-1">
              <select
                value={curiosity.subject}
                onChange={e => setCuriosity({ ...curiosity, subject: e.target.value })}
                className="w-full border p-1 rounded"
              >
                {subjectsByGrade[profile.grade] && 
                  Object.keys(subjectsByGrade[profile.grade]).map(subjectKey => (
                    <option key={subjectKey} value={subjectKey}>
                      {subjectNameMap[subjectKey] 
                        ? t(subjectNameMap[subjectKey].vi, subjectNameMap[subjectKey].en) 
                        : subjectKey}
                    </option>
                  ))}
              </select>
            </div>
            <AiOutlinePlusCircle
              className="text-blue-600 cursor-pointer text-2xl shrink-0"
              title={t('Thêm môn học', 'Add subject')} 
              onClick={() => { setAddDialogMode('subject'); setAddDialogOpen(true); }}
            />
          </div>

          {/* <label className="block text-sm font-medium flex items-center">
            {t('Chủ đề', 'Topics')}
            <AiOutlinePlusCircle
              className="ml-2 text-blue-600 cursor-pointer text-2xl"
              title={t('Thêm chủ đề', 'Add topic')}
              onClick={() => { setAddDialogMode('topic'); setAddDialogOpen(true); }}
            />
          </label> */}
          <select 
            multiple 
            size={10} 
            className="border p-1 w-full rounded" 
            value={curiosity.topics}
            onChange={e => {
              const selected = Array.from(e.target.selectedOptions, o => o.value);
              setCuriosity(prev => ({
                ...prev,
                topics: selected
              }));
            }}
          >
            {subjectsByGrade[profile.grade]?.[curiosity.subject]?.map(topic => (
              <option key={topic.vi} value={topic.en}>
                {t(topic.vi, topic.en)}
              </option>
            ))}
          </select>
        </div>

        {/* Panel P3 - Curiosity */}
        <div className="border rounded-xl p-4 shadow w-1/2 flex flex-col justify-between">
          <h2 className="flex items-center font-semibold text-lg mb-2">
            {t('Tùy chọn lăng kính tò mò', 'Curiosity Options')}
            <AiOutlinePlusCircle
              className="ml-2 text-blue-600 cursor-pointer text-2xl"
              title={t('Thêm mong muốn tò mò', 'Add new curiosity intention')}
              onClick={() => { setAddDialogMode('curiosityType'); setAddDialogOpen(true); }}
            />
          </h2>
          <div className="grid grid-cols-2 gap-2 h-full">
            <div className="col-span-1 flex flex-col h-full">
              {/* <label className="block mb-1 text-sm font-medium flex items-center">
                {t('Tôi tò mò về', 'Curiosity Intentions')}
                
              </label> */}
              <select
                multiple
                size={6}
                value={curiosity.types}
                onChange={e => {
                  // Lưu value là index dạng string
                  const vals = Array.from(e.target.selectedOptions, o => o.value);
                  setCuriosity({ ...curiosity, types: vals });
                }}
                className="border p-1 w-full rounded flex-1"
              >
                {curiosityTypesList.map((item, idx) => (
                  <option key={item.vi} value={idx.toString()}>{t(item.vi, item.en)}</option>
                ))}
              </select>
            </div>
            <div className="col-span-1 flex flex-col h-full">
              <div className="flex items-center mb-2">
                <label className="block text-sm font-medium mr-2 min-w-[70px]">{t('Mục tiêu', 'Purpose')}</label>
                <select value={curiosity.goal} onChange={e => setCuriosity({ ...curiosity, goal: e.target.value })} className="border p-1 rounded flex-1">
                  <option value="Review & Practice">{t('Ôn tập', 'Review & Practice')}</option>
                  <option value="Discovery">{t('Khám phá', 'Discovery')}</option>
                  <option value="Research">{t('Nghiên cứu', 'Research')}</option>
                </select>
              </div>
              <div className="flex items-center mb-2">
                <label className="block text-sm font-medium mr-2 min-w-[70px]">{t('Mức độ', 'Difficulty')}</label>
                <select value={curiosity.difficulty} onChange={e => setCuriosity({ ...curiosity, difficulty: e.target.value })} className="border p-1 rounded flex-1">
                  <option value="Easy">{t('Dễ', 'Easy')}</option>
                  <option value="Medium">{t('Trung bình', 'Medium')}</option>
                  <option value="Hard">{t('Khó', 'Hard')}</option>
                </select>
              </div>
              <label className="block mb-1 text-sm font-medium">{t('Mong muốn', 'Expectation')}</label>
              <textarea
                placeholder={t('Mong muốn phát huy trí tò mò khác...', 'Curiosity Expectation...')}
                className="border w-full p-1 rounded resize-none"
                style={{ minHeight: 20, height: '50%', maxHeight: '100%' }}
                onChange={e => setCuriosity({ ...curiosity, expectation: e.target.value })}
              ></textarea>
            </div>
          </div>
          <div className="flex space-x-4 mt-4">
            <button
              className="bg-blue-600 text-white p-2 rounded shadow flex-1 flex items-center justify-center hover:bg-blue-700"
              onClick={reframeContents}
              disabled={loading}
            >
              {loading && <AiOutlineLoading3Quarters className="animate-spin mr-2" />}
              {t('Dàn nội dung học khơi tò mò', 'Foster Curiosity')}
            </button>

            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="px-2 py-1 bg-blue-500 text-white rounded shadow hover:bg-blue-600"
            >
              {showSidebar ? t('Ẩn lịch sử', 'Hide Timeline') : t('Hiện lịch sử', 'Show Timeline')}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-row px-4 py-4 space-x-4">
        {/* Panel P4 - History */}
        {showSidebar && (
          <div
            className={
              `w-[250px] border rounded-xl p-4 shadow shrink-0` +
              (selectedOutput
                ? ' h-auto max-h-none'
                : ' max-h-[420px] overflow-y-auto')
            }
          >
            <h2 className="font-semibold text-lg">{t('Lịch sử tương tác', 'Timeline')}</h2>
            {firestoreError ? (
              <div className="text-red-600 text-sm ">{firestoreError}</div>
            ) : (
              <ul className="text-sm pl-2 space-y-2">
                {history.slice(0, history.length).map((h, idx) => (
                  <li
                    key={h.id || h.timestamp}
                    className="group flex items-start justify-between cursor-pointer hover:underline text-blue-700"
                  >
                    <div
                      className="flex-1 min-w-0"
                      onClick={() => setSelectedOutput(h.content)}
                    >
                      <div className="font-medium truncate">{h.content.slice(0, 80)}...</div>
                      <div className="text-xs text-gray-500">
                        {
                          (() => {
                            const d = new Date(h.timestamp);
                            // d.setDate(d.getDate() - 20);
                            return d.toLocaleString();
                          })()
                        }
                      </div>
                    </div>
                    <button
                      className="ml-2 text-red-500 opacity-60 hover:opacity-100"
                      title={t("Xoá", "Delete")}
                      onClick={e => { e.stopPropagation(); handleDeleteHistory(h); }}
                    >
                      <AiOutlineDelete />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Panel P5 - Output rehypeRaw */}
        <div className="flex-1 border rounded-xl p-4 shadow">
          {/* <h2 className="font-semibold text-lg mb-2">{t('Kết quả', 'Output')}</h2> */}
          <div onContextMenu={handleContextMenu} className="prose max-w-none text-base leading-relaxed">
            {selectedOutput ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeHighlight, rehypeKatex, rehypeRaw]}
                components={{
                  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold my-4" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="text-xl font-bold my-3" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-lg font-bold my-2" {...props} />,
                  p: ({ node, ...props }) => <p className="my-2" {...props} />,
                  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
                  em: ({ node, ...props }) => <em className="italic" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-2" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-2" {...props} />,
                  li: ({ node, ...props }) => <li className="my-1" {...props} />,
                  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />
                }}
              >
                {selectedOutput}
              </ReactMarkdown>
            ) : (
              <p className="text-gray-500 italic">{t('Kết quả sẽ hiển thị ở đây...', 'Results will appear here...')}</p>
            )}
            {showLLMPopup && (
              <AskLLMPopup
                requestData={{
                  age: profile.age ? parseInt(profile.age) : 16,
                  name: profile.name, // || "anonymous",
                  personality: profile.personality, // || "curiosity, proactive, open-minded",
                  interest: profile.interest, // || "freedom, creativity, technology",
                  style: profile.style === 'friendly' ? "use mày/tao, friendly" : profile.style,
                  llm_api: profile.llm_api,
                  language: profile.language === 'vi' ? 'vietnamese' : 'english',
                  grade: parseInt(profile.grade), 
                  subject: subjectNameMap[curiosity.subject]?.en || curiosity.subject,
                  topics: curiosity.topics.join(', ') || 'Calculus, Differential Equations',
                  curiosity_goal: curiosity.goal,
                  curiosity_level: curiosity.difficulty,
                  curiosity_types: curiosity.types.join(', ') || 'Open-ended questions and hypothetical scenarios',
                  expectation: curiosity.expectation, 
                  query: curiosity.query, 
                }}
                onClose={() => setShowLLMPopup(false)} // Đóng popup khi hoàn tất
              />
            )}
          </div>
        </div>
      </div>
      {/* Dialog About */}
      <AboutDialog isOpen={showAbout} onClose={() => setShowAbout(false)} />
      {/* Dialog thêm mới subject/topic/curiosity type */}
      <AddItemDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAddSubject={handleAddSubject}
        onAddTopic={handleAddTopic}
        onAddCuriosityType={handleAddCuriosityType}
        mode={addDialogMode}
        setMode={setAddDialogMode}
        newSubject={newSubject} setNewSubject={setNewSubject}
        newSubjectEn={newSubjectEn} setNewSubjectEn={setNewSubjectEn}
        newTopic={newTopic} setNewTopic={setNewTopic}
        newTopicEn={newTopicEn} setNewTopicEn={setNewTopicEn}
        newCuriosityType={newCuriosityType} setNewCuriosityType={setNewCuriosityType}
        newCuriosityTypeEn={newCuriosityTypeEn} setNewCuriosityTypeEn={setNewCuriosityTypeEn}
        t={t}
      />
      <footer className="w-full bg-blue-600 text-white mt-12">
        <div className="max-w-5xl mx-auto px-4 py-3 flex justify-between items-center"> {/* Dùng flex để gộp các mục trên 1 hàng */}
          <p className="text-xs">&copy; {new Date().getFullYear()} AI Copilot. All rights reserved.</p>
          <p className="text-xs">Developped by <span className="font-semibold"> Helix and AI-Team!</span></p>
          {/* <a
            href="https://twitter.com/helix"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center space-x-1 hover:text-gray-200 transition"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M22.46 6c-.77.35-1.5.58-2.27.69a4.1 4.1 0 0 0 1.8-2.27c-.77.46-1.62.81-2.5.98a4.14 4.14 0 0 0-7.04 3.77A11.74 11.74 0 0 1 3.15 4.8a4.14 4.14 0 0 0 1.28 5.53c-.66 0-1.28-.2-1.83-.5v.05a4.14 4.14 0 0 0 3.32 4.06c-.45.12-.91.18-1.38.18-.33 0-.67-.03-1-.1a4.15 4.15 0 0 0 3.86 2.87 8.3 8.3 0 0 1-5.1 1.76c-.33 0-.66-.02-.98-.06a11.75 11.75 0 0 0 6.29 1.85c7.55 0 11.68-6.26 11.68-11.68 0-.18 0-.36-.01-.54A8.36 8.36 0 0 0 24 4.56a8.15 8.15 0 0 1-2.34.64 4.1 4.1 0 0 0 1.8-2.27Z"/>
            </svg>
            <span class="text-xs">X / @helix</span>
          </a> */}
        </div>
      </footer>
    </>
  );
};

const AiCopilot = () => (
  <AuthProvider>
    <AiCopilotContent />
  </AuthProvider>
);

export default AiCopilot;
